import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_SEARCH_DEBOUNCE_MS = 400;

export interface UseDebouncedUrlSearchOptions {
  committedQuery?: string;
  debounceMs?: number;
  minimumAutoSearchLength?: number;
  onCommit?: (query: string | undefined) => void;
}

export function normalizeSearchQuery(value: string): string | undefined {
  const normalizedQuery = value.trim();
  return normalizedQuery.length > 0 ? normalizedQuery : undefined;
}

/**
 * Keeps a locally editable search draft synchronized with URL-owned committed state.
 * Acknowledged commits cannot overwrite newer typing, while external URL navigation wins.
 */
export function useDebouncedUrlSearch({
  committedQuery,
  debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  minimumAutoSearchLength = 1,
  onCommit,
}: UseDebouncedUrlSearchOptions) {
  const [draft, setDraft] = useState(committedQuery ?? '');
  const [isComposing, setIsComposing] = useState(false);
  const pendingCommitRef = useRef<{ query: string | undefined } | null>(null);
  const normalizedDraft = normalizeSearchQuery(draft);
  const normalizedCommittedQuery = normalizeSearchQuery(committedQuery ?? '');
  const hasPendingQuery = (normalizedDraft ?? '') !== (normalizedCommittedQuery ?? '');
  const isCommitPending = onCommit !== undefined && hasPendingQuery;

  useEffect(() => {
    const pendingCommit = pendingCommitRef.current;
    const isPendingCommitAcknowledged =
      pendingCommit !== null && (pendingCommit.query ?? '') === (normalizedCommittedQuery ?? '');

    pendingCommitRef.current = null;
    setDraft((currentDraft) => {
      // URL acknowledgement must not trim the editable draft or move its caret.
      if (isPendingCommitAcknowledged) {
        return currentDraft;
      }

      return normalizedCommittedQuery ?? '';
    });
  }, [normalizedCommittedQuery]);

  const canAutoCommit =
    normalizedDraft === undefined || Array.from(normalizedDraft).length >= minimumAutoSearchLength;
  const submit = useCallback(() => {
    if (!isCommitPending || isComposing) return;
    const pending = pendingCommitRef.current;
    if (pending && pending.query === normalizedDraft) return;
    pendingCommitRef.current = { query: normalizedDraft };
    onCommit(normalizedDraft);
  }, [isCommitPending, isComposing, normalizedDraft, onCommit]);

  useEffect(() => {
    if (!isCommitPending || isComposing || !canAutoCommit) return undefined;
    const debounceId = window.setTimeout(submit, debounceMs);
    return () => {
      window.clearTimeout(debounceId);
    };
  }, [canAutoCommit, debounceMs, isCommitPending, isComposing, submit]);

  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);

  const handleCompositionEnd = useCallback((value: string) => {
    setDraft(value);
    setIsComposing(false);
  }, []);

  return {
    draft,
    isTransitionPending: isCommitPending && !isComposing && canAutoCommit,
    onCompositionEnd: handleCompositionEnd,
    onCompositionStart: handleCompositionStart,
    setDraft,
    submit,
  };
}
